"use client";

import { useDisclosure } from "@chakra-ui/react";
import { signOut } from "next-auth/react";
import { useCallback, useMemo, useState } from "react";
import { useMediaAssetGet } from "@/features/media/hooks";
import { useTenantData } from "@/hooks";
import { EntryType, MediaGroup } from "@/openapi/generated";

const MODAL_MORPH_MS = 450;
const OVERLAY_START_MS = 120;

type LogoutPhase = "idle" | "modal-loading" | "overlay";

type UseLogoutOptions = {
  callbackUrl: string;
  /** When false, tenant logo can load without auth (public navbar). */
  logoRequireAuth?: boolean;
};

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function useLogout({
  callbackUrl,
  logoRequireAuth = true,
}: UseLogoutOptions) {
  const [phase, setPhase] = useState<LogoutPhase>("idle");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { tenantData, tenantId } = useTenantData();

  const mediaAssetParams = useMemo(
    () => ({
      tenantId,
      ownerType: EntryType.Tenant,
      ownerId: tenantId,
      mediaGroup: MediaGroup.Logo,
    }),
    [tenantId],
  );

  const { url: logoUrl } = useMediaAssetGet(mediaAssetParams, {
    enabled: !!tenantId && tenantId > 0,
    requireAuth: logoRequireAuth,
  });

  const handleLogout = useCallback(async () => {
    if (phase !== "idle") return;

    setPhase("modal-loading");
    await delay(MODAL_MORPH_MS);
    onClose();
    await delay(OVERLAY_START_MS);
    setPhase("overlay");
    await signOut({ callbackUrl });
  }, [callbackUrl, onClose, phase]);

  const handleCloseModal = useCallback(() => {
    if (phase !== "idle") return;
    onClose();
  }, [onClose, phase]);

  const handleOpenModal = useCallback(() => {
    setPhase("idle");
    onOpen();
  }, [onOpen]);

  return {
    isLogoutModalOpen: isOpen,
    onLogoutModalOpen: handleOpenModal,
    onLogoutModalClose: handleCloseModal,
    handleLogout,
    isLoggingOut: phase === "modal-loading",
    showLogoutOverlay: phase === "overlay",
    logoUrl,
    tenantData,
  };
}
